import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">文章不存在</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 2);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',爆瓜网,吃瓜,爆料'}
        canonicalUrl={`https://baogua.com/article/${slug}`}
        ogType="article"
        ogImage={article.heroImage}
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.heroImage,
            datePublished: article.date,
            author: { '@type': 'Person', name: article.author },
            publisher: {
              '@type': 'Organization',
              name: '爆瓜网',
              url: 'https://baogua.com',
            },
            url: `https://baogua.com/article/${slug}`,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://baogua.com/article/${slug}`,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baogua.com' },
              { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://baogua.com/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://baogua.com/article/${slug}` },
            ],
          },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative h-[320px] md:h-[450px] w-full overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              title={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
            <div className="relative z-10 h-full w-full flex items-end pb-8 md:pb-12">
              <div className="px-4 md:px-6 w-full">
                <div className="max-w-7xl mx-auto">
                  <div className="max-w-3xl">
                    <span className="inline-block px-3 py-1 border border-white/40 rounded-full text-[10px] md:text-xs text-white/90 font-label mb-3 md:mb-4">
                      {article.categoryName}
                    </span>
                    <h1 className="text-xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight">
                      {article.title}
                    </h1>
                    <div className="mt-3 md:mt-4 flex flex-wrap items-center gap-3 text-xs md:text-sm text-white/70 font-label">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                        {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="px-4 md:px-6 py-4 md:py-5 bg-background-100/50 border-b border-background-200/50">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 font-label" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                  {article.categoryName}
                </Link>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
              </nav>
            </div>
          </div>

          <section className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <article className="lg:w-[65%]">
                  <div className="flex flex-wrap gap-2 mb-6">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 text-xs font-medium rounded-full bg-background-100 text-foreground-600 font-label whitespace-nowrap">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="prose-custom">
                    {article.content.map((block, idx) => (
                      <p key={idx} className="text-sm md:text-base text-foreground-800 font-body leading-relaxed mb-5">
                        {block.text}
                      </p>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 font-label">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                        {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(0)}万次阅读` : `${article.viewCount}次阅读`}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center"></i>
                        {article.commentCount >= 10000 ? `${(article.commentCount / 10000).toFixed(0)}万条评论` : `${article.commentCount}条评论`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 font-label">分享：</span>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                        <i className="ri-weibo-line text-xs"></i>
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                        <i className="ri-wechat-line text-xs"></i>
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                        <i className="ri-link text-xs"></i>
                      </button>
                    </div>
                  </div>
                </article>

                <aside className="lg:w-[35%]">
                  <div className="sticky top-24">
                    <div className="bg-background-100/50 rounded-xl p-5 md:p-6 border border-background-200/50">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                        <span className="text-xs font-semibold text-foreground-800 font-label tracking-wide">前线瓜友</span>
                      </div>
                      <blockquote className="text-base md:text-lg font-heading font-bold text-foreground-950 leading-relaxed mb-4">
                        &ldquo;视频证据已核验，完整时间线整理，无公关纯爆料。&rdquo;
                      </blockquote>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-800">{article.author}</span>
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-verified-badge-fill text-accent-500 text-sm"></i>
                        </span>
                        <span className="text-xs text-foreground-500 font-label">实名爆料</span>
                      </div>
                    </div>

                    {relatedArticles.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-foreground-800 font-label mb-3">
                          相关爆料
                        </h4>
                        <div className="flex flex-col gap-3">
                          {relatedArticles.map((ra) => (
                            <Link
                              key={ra.id}
                              to={`/article/${ra.slug}`}
                              className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            >
                              <img
                                src={ra.thumbnail}
                                alt={ra.title}
                                className="w-16 h-16 rounded-lg object-cover object-top shrink-0"
                              />
                              <div className="min-w-0">
                                <h5 className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                                  {ra.title}
                                </h5>
                                <p className="mt-1 text-[10px] text-foreground-500 font-label">{ra.date}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <section className="flex flex-col lg:flex-row min-h-[350px] md:min-h-[400px] border-t border-background-200/50">
            <div className="lg:w-1/2 relative overflow-hidden bg-accent-50">
              <img
                src="https://readdy.ai/api/search-image?query=Mysterious%20silhouette%20holding%20smartphone%20with%20breaking%20news%20notification%20on%20screen%2C%20dark%20dramatic%20lighting%2C%20suspense%20atmosphere%2C%20cinematic%20style%2C%20editorial%20photography%2C%20high%20contrast%2C%20warm%20amber%20glow%2C%20moody%20composition&width=960&height=700&seq=article-cta-01&orientation=portrait"
                alt="更多实锤爆料"
                title="后续黑料 - 下载APP查看完整内容"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 left-4 md:bottom-10 md:left-8">
                <h3 className="text-2xl md:text-4xl font-heading font-black text-foreground-950 leading-tight">
                  更多实锤
                  <br />
                  等你揭晓
                </h3>
              </div>
            </div>

            <div className="lg:w-1/2 flex flex-col items-center justify-center px-4 md:px-6 py-12 md:py-0 bg-background-50">
              <div className="max-w-md text-center">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
                  这条瓜还有后续
                </h2>
                <p className="mt-3 text-sm md:text-base text-foreground-600 font-body leading-relaxed">
                  下载爆瓜网APP查看完整视频与高清图集，第一时间获取最新进展和后续爆料。支持实时推送，绝不错过任何一个大瓜。
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="#"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-apple-fill w-4 h-4 flex items-center justify-center"></i>
                    iOS 下载
                  </a>
                  <a
                    href="#"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-foreground-800 font-semibold text-sm rounded-full hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-android-fill w-4 h-4 flex items-center justify-center"></i>
                    Android 下载
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}